import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../useDebounce';

const SEARCH_DEBOUNCE_MS = 500;
// Items per page in the side panel (8 rows of its 2-column grid).
export const LIBRARY_PAGE_SIZE = 16;

interface PageResponse<Row> {
  count: number;
  results: Row[];
}

interface UseCanvasLibraryPageOptions<Row, Item> {
  search: string;
  fetchPage: (params: { page: number; page_size: number; search?: string }) => Promise<PageResponse<Row>>;
  toItem: (row: Row) => Item;
  errorMessage: string;
}

// One page of a library panel (templates, SKUs), with the search and pagination the table pages use.
// A new search goes back to the first page. totalPages keeps its last value while a page loads, so the
// pagination doesn't vanish and come back on every page change.
export const useCanvasLibraryPage = <Row, Item>({
  search,
  fetchPage,
  toItem,
  errorMessage,
}: UseCanvasLibraryPageOptions<Row, Item>) => {
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Item[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Read through refs: they're new functions on every render of the caller.
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const toItemRef = useRef(toItem);
  toItemRef.current = toItem;
  const lastSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    // Search changed on another page: go back to the first one, which fetches it.
    if (lastSearchRef.current !== debouncedSearch) {
      lastSearchRef.current = debouncedSearch;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchPageRef
      .current({ page, page_size: LIBRARY_PAGE_SIZE, search: debouncedSearch || undefined })
      .then((response) => {
        if (cancelled) return;
        setItems(response.results.map(toItemRef.current));
        setTotalPages(Math.ceil(response.count / LIBRARY_PAGE_SIZE));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setItems([]);
        setTotalPages(0);
        setError(err instanceof Error ? err.message : errorMessage);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, page, errorMessage]);

  const goToPage = useCallback((next: number) => setPage(Math.max(1, next)), []);

  return { items, isLoading, error, page, totalPages, setPage: goToPage };
};
